/**
 * TanStack mutation factories — Phase 4-2 / T9.
 *
 * Each factory takes a QueryClient (so it can invalidateQueries) plus the
 * resource scope (e.g. conversationId) and returns a `createMutation`-ready
 * options object.
 *
 * Importers: Phase 4-3 composer / host-action components call
 * `createMutation(sendMessageMutation(queryClient, conversationId))`.
 */

import type { QueryClient } from '@tanstack/svelte-query';
import { http } from './http.js';
import type { Message, Note, Tag } from './queries.js';

// ---------------------------------------------------------------------------
// Send a text message
// ---------------------------------------------------------------------------

export interface SendMessageInput {
  text: string;
  /** Optional client-generated id used to deduplicate optimistic inserts. */
  clientId?: string;
  /**
   * Phase 2b — attach a `user_phone_number` Quick Reply chip to the outbound
   * message so the customer can one-tap-share their phone. The composer
   * surfaces this as a checkbox that unsets after one send.
   */
  requestPhoneNumber?: boolean;
  /**
   * M4.1 — when set, sends an attachment message referencing this stable
   * public attachmentUrl (returned by {@link uploadAttachment}); `text` is
   * ignored.
   */
  attachmentUrl?: string;
  /**
   * M4.6 — which kind of attachment. Defaults to 'image' for callers written
   * before video existed. It travels because the server cannot recover it:
   * our object keys carry no extension, and Meta has to be told whether to
   * render a photo or a player.
   */
  attachmentKind?: 'image' | 'video';
  /**
   * M4.1 — optimistic-only local object URL (URL.createObjectURL) so the
   * image renders instantly before the server round-trip. The composer
   * revokes it once the send settles.
   */
  previewUrl?: string;
}

// M4.1 / M4.4 — upload allowlists + caps, mirrored from @platform/contracts.
// The widget does not depend on the contracts package, so these are copies —
// and a copy of a rule is how two sides drift apart without either being
// wrong on its own. `attachment-limits-mirror.test.ts` reads BOTH files and
// fails when they disagree, which is the only thing that makes a mirror safe.
export const IMAGE_MIME_ALLOWLIST = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;
export const VIDEO_MIME_ALLOWLIST = ['video/mp4', 'video/quicktime', 'video/webm', 'video/3gpp'] as const;
export const IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const VIDEO_MAX_BYTES = 25 * 1024 * 1024;

/** Which cap applies to a file, or null when the type is not accepted. */
export function maxBytesForFile(type: string): number | null {
  const t = type.split(';', 1)[0]!.trim().toLowerCase();
  if ((IMAGE_MIME_ALLOWLIST as readonly string[]).includes(t)) return IMAGE_MAX_BYTES;
  if ((VIDEO_MIME_ALLOWLIST as readonly string[]).includes(t)) return VIDEO_MAX_BYTES;
  return null;
}

interface AttachmentUploadResponse {
  attachmentUrl: string;
}

/**
 * M4.1 — upload an operator image for an outbound message.
 *
 * One hop: POST the file bytes to messages-service through the gateway, like
 * every other call, and get back the stable public `attachmentUrl` to pass to
 * the send mutation.
 *
 * **This used to be two hops, the second one a presigned PUT straight to the
 * bucket, and it never worked in production** — a cross-origin PUT needs CORS
 * on the bucket, which answered every preflight with 403 and no CORS headers.
 * The browser now touches no other origin.
 *
 * Type + size are still checked here so an operator learns immediately, but
 * the server checks both again: this copy exists for the message, not for the
 * safety.
 */
export async function uploadAttachment(conversationId: string, file: File): Promise<string> {
  const maxBytes = maxBytesForFile(file.type);
  if (maxBytes === null) {
    throw new Error('Unsupported file type. Use a JPEG, PNG, GIF or WebP image, or an MP4, MOV, WebM or 3GP video.');
  }
  if (file.size > maxBytes) {
    // Named per kind: "too large (max 8 MB)" on a 20 MB video would be a lie,
    // and the operator would shrink a file that was never the problem.
    const kind = maxBytes === IMAGE_MAX_BYTES ? 'Image' : 'Video';
    throw new Error(`${kind} is too large (max ${Math.floor(maxBytes / (1024 * 1024))} MB).`);
  }
  const uploaded = await http.postBytes<AttachmentUploadResponse>(
    `/api/conversations/${conversationId}/attachments`,
    file,
    file.type,
  );
  return uploaded.attachmentUrl;
}

export interface SendMessageContext {
  previous: Message[] | undefined;
  optimistic: Message;
}

interface RawMessageResponse {
  id: string;
  conversationId: string;
  direction?: string;
  messageType?: string;
  body?: string;
  text?: string;
  attachmentUrl?: string;
  createdAt: string;
  reactions?: Message['reactions'] | null;
  authorDisplayName?: string | null;
  authorProfilePicUrl?: string | null;
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeSendResponse(raw: RawMessageResponse): Message {
  const direction = (raw.direction ?? '').toLowerCase();
  const t = (raw.messageType ?? 'TEXT').toLowerCase();
  return {
    id: raw.id,
    conversationId: raw.conversationId,
    direction: direction === 'inbound' ? 'inbound' : 'outbound',
    type: (['text', 'image', 'video', 'system'] as const).includes(
      t as 'text' | 'image' | 'video' | 'system',
    )
      ? (t as Message['type'])
      : 'text',
    text: raw.text ?? raw.body,
    attachmentUrl: raw.attachmentUrl,
    createdAt: raw.createdAt,
    reactions: raw.reactions ?? undefined,
    authorDisplayName: raw.authorDisplayName ?? null,
    authorProfilePicUrl: raw.authorProfilePicUrl ?? null,
  };
}

export const sendMessageMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'send'],
  mutationFn: async (input: SendMessageInput): Promise<Message> => {
    const idempotencyKey = input.clientId ?? newIdempotencyKey();
    const payload = input.attachmentUrl
      ? {
          idempotencyKey,
          type: input.attachmentKind ?? ('image' as const),
          attachmentUrl: input.attachmentUrl,
        }
      : {
          idempotencyKey,
          type: 'text' as const,
          body: input.text,
          ...(input.requestPhoneNumber ? { requestPhoneNumber: true } : {}),
        };
    const raw = await http.post<RawMessageResponse>(
      `/api/conversations/${conversationId}/messages`,
      payload,
    );
    return normalizeSendResponse(raw);
  },

  onMutate: async (input: SendMessageInput): Promise<SendMessageContext> => {
    const key = ['conversations', conversationId, 'messages'] as const;
    await queryClient.cancelQueries({ queryKey: key });

    const previous = queryClient.getQueryData<Message[]>(key);
    const optimistic: Message = input.attachmentUrl
      ? {
          id: input.clientId ?? `optimistic-${Date.now()}`,
          conversationId,
          direction: 'outbound',
          type: input.attachmentKind ?? 'image',
          // Prefer the local object URL for an instant preview; fall back to
          // the (already-uploaded) public URL.
          attachmentUrl: input.previewUrl ?? input.attachmentUrl,
          createdAt: new Date().toISOString(),
        }
      : {
          id: input.clientId ?? `optimistic-${Date.now()}`,
          conversationId,
          direction: 'outbound',
          type: 'text',
          text: input.text,
          createdAt: new Date().toISOString(),
        };

    queryClient.setQueryData<Message[]>(key, (old) => [...(old ?? []), optimistic]);

    return { previous, optimistic };
  },

  onError: (_err: unknown, _input: SendMessageInput, ctx: SendMessageContext | undefined) => {
    if (!ctx) return;
    const key = ['conversations', conversationId, 'messages'];
    queryClient.setQueryData<Message[]>(key, ctx.previous);
  },

  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['conversations', conversationId, 'messages'] });
  },
});

// ---------------------------------------------------------------------------
// Sender action (typing indicator, mark-seen) — Phase 1 of chat affordances
// ---------------------------------------------------------------------------

export type SenderAction = 'typing_on' | 'typing_off' | 'mark_seen';

/**
 * Fire a Messenger sender action for a conversation. Best-effort: a failed
 * call is logged and swallowed — typing is ephemeral and queuing/retrying
 * would defeat the UX. No mutation wrapper because there is no optimistic
 * state to roll back.
 */
export async function sendSenderAction(
  conversationId: string,
  action: SenderAction,
): Promise<void> {
  try {
    await http.post<void>(
      `/api/conversations/${conversationId}/sender-action`,
      { action },
    );
  } catch (err) {
    // Don't surface — operator UX shouldn't twitch over a typing call.
    console.warn('[widget] sendSenderAction failed', action, err);
  }
}

// ---------------------------------------------------------------------------
// React on a customer message (Phase 4 outbound reactions)
// ---------------------------------------------------------------------------

export interface ReactToMessageInput {
  messageId: string;
  /** One of the 7 Messenger-supported emojis, or null to clear. */
  emoji: string | null;
}

export interface ReactToMessageContext {
  previous: Message[] | undefined;
}

/**
 * Operator reacts (or unreacts) on a customer message. Optimistically patches
 * the `reactions.operator` slot so the pill appears immediately; rolls back
 * on error. The realtime `message.reaction-updated` frame the server emits on
 * success ends up invalidating the same query — TanStack dedupes the refetch.
 */
export const reactToMessageMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'react'],
  mutationFn: async (input: ReactToMessageInput): Promise<void> => {
    await http.post<void>(
      `/api/conversations/${conversationId}/messages/${input.messageId}/reactions`,
      { emoji: input.emoji },
    );
  },

  onMutate: async (input: ReactToMessageInput): Promise<ReactToMessageContext> => {
    const key = ['conversations', conversationId, 'messages'] as const;
    await queryClient.cancelQueries({ queryKey: key });

    const previous = queryClient.getQueryData<Message[]>(key);
    queryClient.setQueryData<Message[]>(key, (old) => {
      if (!old) return old;
      return old.map((m) => {
        if (m.id !== input.messageId) return m;
        const current = m.reactions ?? {};
        let next: Message['reactions'];
        if (input.emoji === null) {
          if (!current.operator) return m;
          const rest: NonNullable<Message['reactions']> = {};
          if (current.customer) rest.customer = current.customer;
          next = Object.keys(rest).length > 0 ? rest : undefined;
        } else {
          next = { ...current, operator: { emoji: input.emoji, at: new Date().toISOString() } };
        }
        return { ...m, reactions: next };
      });
    });
    return { previous };
  },

  onError: (
    _err: unknown,
    _input: ReactToMessageInput,
    ctx: ReactToMessageContext | undefined,
  ) => {
    if (!ctx) return;
    const key = ['conversations', conversationId, 'messages'];
    queryClient.setQueryData<Message[]>(key, ctx.previous);
  },

  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['conversations', conversationId, 'messages'] });
  },
});

// ---------------------------------------------------------------------------
// Create a host-side entity (e.g. customer record) from this conversation
// ---------------------------------------------------------------------------

export interface CreateHostEntityInput {
  entityType: string;
  prefill?: Record<string, unknown>;
}

export interface CreateHostEntityResult {
  entityType: string;
  entityId: string;
}

export const createHostEntityMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'host-entity'],
  mutationFn: (input: CreateHostEntityInput): Promise<CreateHostEntityResult> =>
    http.post<CreateHostEntityResult>(`/api/conversations/${conversationId}/host-entities`, input),

  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['conversations', conversationId, 'host-summary'] });
  },
});

// ---------------------------------------------------------------------------
// G1.7 — tag catalog + assignment mutations
// ---------------------------------------------------------------------------

/**
 * Apply a catalog tag to a conversation. Server is idempotent (204 even
 * when already applied), so no optimistic state needed — invalidate the
 * conversation list on settle so the chip strip + header pill refresh.
 * The server-emitted `conversation.tag.updated` realtime frame also
 * invalidates the same query (App.svelte handler from G1.6); TanStack
 * dedupes the refetch.
 */
export const applyTagMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'tags', 'apply'],
  mutationFn: async (input: { tagId: string }): Promise<void> => {
    await http.post<void>(`/api/conversations/${conversationId}/tags`, input);
  },
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});

/** Remove an applied tag from a conversation. Idempotent server-side. */
export const removeTagMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'tags', 'remove'],
  mutationFn: async (input: { tagId: string }): Promise<void> => {
    await http.delete<void>(`/api/conversations/${conversationId}/tags/${input.tagId}`);
  },
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});

/**
 * Create a new tag in the per-tenant catalog. Server returns the full
 * Tag; we invalidate `['tags']` so any open picker / filter bar / manage
 * modal reads the fresh list. Conversations also invalidate because a
 * common follow-up is to apply the freshly-created tag.
 */
export const createTagMutation = (queryClient: QueryClient) => ({
  mutationKey: ['tags', 'create'],
  mutationFn: (input: { name: string; color: string }): Promise<Tag> =>
    http.post<Tag>('/api/tags', input),
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['tags'] });
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});

/**
 * Rename / recolor a catalog tag. Server regenerates slug on rename and
 * returns 409 on collision — callers surface the error inline. Both
 * patches are optional but at least one is server-required.
 */
export const updateTagMutation = (queryClient: QueryClient) => ({
  mutationKey: ['tags', 'update'],
  mutationFn: (input: { id: string; name?: string; color?: string }): Promise<Tag> => {
    const { id, ...patch } = input;
    return http.put<Tag>(`/api/tags/${id}`, patch);
  },
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['tags'] });
    // Conversation chip strips embed name + color, so they need a refresh
    // too. Server doesn't emit `conversation.tag.updated` on catalog
    // edits today; this invalidation is the client-side workaround.
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});

/** Delete a catalog tag; server cascade-removes all assignments. */
export const deleteTagMutation = (queryClient: QueryClient) => ({
  mutationKey: ['tags', 'delete'],
  mutationFn: async (input: { id: string }): Promise<void> => {
    await http.delete<void>(`/api/tags/${input.id}`);
  },
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['tags'] });
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});

// ---------------------------------------------------------------------------
// G2.3 — operator-only conversation notes
// ---------------------------------------------------------------------------

export interface CreateNoteContext {
  previous: Note[] | undefined;
  optimistic: Note;
}

/**
 * Append a note to a conversation. Optimistic prepend so the row appears
 * immediately; reconciles via the server response in onSuccess (the
 * server-emitted realtime conversation.note.created frame also
 * invalidates the same key — TanStack dedupes the refetch).
 */
export const createNoteMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'notes', 'create'],
  mutationFn: (input: { body: string }): Promise<Note> =>
    http.post<Note>(`/api/conversations/${conversationId}/notes`, input),

  onMutate: async (input: { body: string }): Promise<CreateNoteContext> => {
    const key = ['conversations', conversationId, 'notes'] as const;
    await queryClient.cancelQueries({ queryKey: key });

    const previous = queryClient.getQueryData<Note[]>(key);
    const optimistic: Note = {
      id: `optimistic-${Date.now()}`,
      conversationId,
      tenantId: '',
      authorUserId: '',
      // Null name renders as "Operator" — sender label is wrong by id
      // anyway during the brief optimistic window; the real one lands on
      // onSuccess. Using null avoids a misleading "You" label.
      authorDisplayName: null,
      body: input.body,
      createdAt: new Date().toISOString(),
    };
    queryClient.setQueryData<Note[]>(key, (old) => [optimistic, ...(old ?? [])]);
    return { previous, optimistic };
  },

  onError: (_err: unknown, _input: { body: string }, ctx: CreateNoteContext | undefined) => {
    if (!ctx) return;
    const key = ['conversations', conversationId, 'notes'];
    queryClient.setQueryData<Note[]>(key, ctx.previous);
  },

  onSuccess: (created: Note, _input: { body: string }, ctx: CreateNoteContext | undefined) => {
    if (!ctx) return;
    // Swap the optimistic placeholder for the server-confirmed row so
    // authorDisplayName + the real id reflect immediately. Realtime
    // frame may still arrive and invalidate — TanStack dedupes.
    const key = ['conversations', conversationId, 'notes'];
    queryClient.setQueryData<Note[]>(key, (cur) => {
      if (!cur) return [created];
      return cur.map((n) => (n.id === ctx.optimistic.id ? created : n));
    });
  },

  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations', conversationId, 'notes'] });
    // Server touches conv updatedAt on note write so the row resurfaces.
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});

/** Hard delete; non-optimistic (server confirms before local removal). */
export const deleteNoteMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'notes', 'delete'],
  mutationFn: async (input: { noteId: string }): Promise<void> => {
    await http.delete<void>(`/api/conversations/${conversationId}/notes/${input.noteId}`);
  },
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations', conversationId, 'notes'] });
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});

// ---------------------------------------------------------------------------
// G4.1 — conversation assignee
// ---------------------------------------------------------------------------

/**
 * G4.1 — assign or release the conversation's assignee. Body shape matches
 * the server's ConversationAssigneeSetRequest:
 *
 *   - { userId: <uuid> } → assign / reassign
 *   - { userId: null }   → release
 *
 * There is no `ttlMs`. Assignments held until somebody changed them as of
 * 2026-09-07; before that a TTL made the row expire on its own, which is how
 * a conversation could change hands while its customer was mid sentence.
 *
 * Server returns 204 on success, 409 when another operator already holds the
 * row. We invalidate the conversation list so the chip strip + filter views
 * refresh. The server-emitted `conversation.assignee.updated` realtime frame
 * invalidates the same key (handler in App.svelte) — TanStack dedupes.
 *
 * Non-optimistic. The picker's "instant commit" UX already feels fast
 * (small payload, single round-trip) and rolling back an optimistic
 * assignee would mean reconciling against another operator's concurrent
 * write, which the server resolves atomically anyway.
 */
export interface SetAssigneeInput {
  userId: string | null;
}
export const setAssigneeMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'assignee', 'set'],
  mutationFn: async (input: SetAssigneeInput): Promise<void> => {
    await http.put<void>(`/api/conversations/${conversationId}/assignee`, input);
  },
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});

// ---------------------------------------------------------------------------
// M5.1 — mark conversation as read
// ---------------------------------------------------------------------------

/**
 * Advance this operator's read-cursor for the conversation to `now()`.
 * Server returns 204; we invalidate the conversation list so the
 * unread-count chip drops to zero in the same tick. Fire-and-forget on
 * error — push-style affordance, an operator shouldn't see a toast if
 * mark-read fails (the next list refetch will eventually reconcile).
 *
 * Server-emitted `conversation.read.updated` realtime frame also
 * invalidates the same key (handler in App.svelte) — TanStack dedupes
 * for the operator's other tabs.
 */
/**
 * Finish a conversation, or take it back.
 *
 * Two endpoints behind one mutation because they are one decision with a
 * direction, and the caller already knows which way it is going. The server
 * answers with the whole row; we throw it away and invalidate instead, so the
 * list and the thread agree rather than each holding their own copy.
 */
export const setConversationStatusMutation = (
  queryClient: QueryClient,
  conversationId: string,
) => ({
  mutationKey: ['conversations', conversationId, 'status'],
  mutationFn: async (next: 'OPEN' | 'CLOSED'): Promise<void> => {
    const verb = next === 'CLOSED' ? 'close' : 'reopen';
    await http.post<unknown>(`/api/conversations/${conversationId}/${verb}`);
  },
  onSettled: () => {
    // BOTH keys. The list is what the inbox draws; the detail is what the
    // thread pane reads its `status` from, and invalidating only the list
    // left the pane holding the pre-close row — the close succeeded, the
    // server agreed, and the screen carried on offering a reply box.
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
  },
});

export const markReadMutation = (queryClient: QueryClient, conversationId: string) => ({
  mutationKey: ['conversations', conversationId, 'mark-read'],
  mutationFn: async (): Promise<void> => {
    await http.post<void>(`/api/conversations/${conversationId}/mark-read`);
  },
  onSettled: () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  },
});
